import React, { LegacyRef } from 'react';
import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';

import { AnchorAPI } from 'client/modules/AnchorAPI';
import CreateAccountContainer from 'client/components/CreateAccountContainer';
import { LoginContainer } from 'client/components/LoginContainer';
import { COLOR_BLACK } from 'client/components/MarketingPagesShared/styles';
import RequestResetPasswordConfirmationContainer from 'client/components/RequestResetPasswordConfirmationContainer';
import RequestResetPasswordContainer from 'client/components/RequestResetPasswordContainer';
import events from 'client/components/SignupPageContainer/events';
import { AnchorLogoWithWordmark } from 'client/components/svgs/AnchorLogoWithWordmark';
import { useT } from 'client/i18n/hooks/useT';
import { paths } from 'client/shared/Paths';
import { INTEGRATION_CONNECTION_MENU_MODE } from 'client/screens/IntegrationConnectionScreen/constants';
import { LinkText } from 'shared/Link';
import { PODCAST_COLORS } from 'client/utils';
import {
  LoginSignupContainer,
  LoginSignupHeading,
  LoginSignupSubheading,
  LoginSignUpOption,
  LoginSignUpOptionsContainer,
  LoginSignUpTerms,
} from './styles';

type LoginSignupProps = {
  currentStatus: INTEGRATION_CONNECTION_MENU_MODE;
  reference: LegacyRef<HTMLDivElement> | undefined;
  setCurrentStatus: (value: INTEGRATION_CONNECTION_MENU_MODE) => void;
};

export function LoginSignUp({
  currentStatus,
  reference,
  setCurrentStatus,
}: LoginSignupProps) {
  const t = useT();
  const isSignup = currentStatus === 'create';

  const subheading = isSignup
    ? t(
        'IntegrationConnectionScreen.LoginSignUp.Subheading.SignUp',
        'Create an account to easily upload and publish your video on Spotify.',
        'The subheading when a user hits the integration connection screen to create an account'
      )
    : t(
        'IntegrationConnectionScreen.LoginSignUp.Subheading.Login',
        'Log in to easily upload and publish your video on Spotify.',
        'The subheading when a user hits the integration connection screen to login to an existing account'
      );

  const redirectLink = (
    <button
      key={
        isSignup
          ? 'integrationConnectionScreenLoginLinkKey'
          : 'integrationConnectionScreenSignUpLinkKey'
      }
      onClick={() => setCurrentStatus(isSignup ? 'login' : 'create')}
    >
      {isSignup
        ? t('SignupPage.LoginLinkText', 'Log in', 'Log in link text')
        : t('Link.Signup', 'Sign up', 'Sign up link text')}
    </button>
  );

  const redirectOption = isSignup
    ? t(
        'SignupForm.LoginInsteadMessage',
        'Already have an account? {loginLink} instead.',
        'Sign up form login instead message',
        { loginLink: redirectLink }
      )
    : t(
        'Login.SignupMessage',
        "Don't have an account? {signupLink} instead.",
        'Login page sign up message',
        { signupLink: redirectLink }
      );

  const importOption = t(
    'IntegrationConnectionScreen.LoginSignUp.Import',
    'You can also { importLink } an existing podcast to Anchor.',
    'The import message to give the option to import their whole podcast via an RSS feed. The full text in English with variables is: You can also move an existing podcast to Anchor.',
    {
      importLink: (
        <LinkText
          key="integrationConnectionScreenImportLink"
          target="_blank"
          isInline={true}
          to="/switch"
          color="gray"
        >
          {t(
            'IntegrationConnectionScreen.LoginSignUp.ImportLink',
            'move',
            'The import link to navigate a user to the switch to import their whole podcast via an RSS feed'
          )}
        </LinkText>
      ),
    }
  );

  const signupTerms = t(
    'SignupForm.TermsOfServiceAgreementMessage',
    'By continuing, you agree to our {termsOfServiceLink} and {privacyPolicyLink}.',
    'Sign up terms of service agreement message',
    {
      termsOfServiceLink: (
        <LinkText
          key="integrationConnectionScreenSignUpTOSLink"
          target="_blank"
          onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
          isInline={true}
          to={paths.legal.tos}
          color="gray"
        >
          {t(
            'Link.TermsOfService',
            'Terms of Service',
            'Terms of Service link text'
          )}
        </LinkText>
      ),
      privacyPolicyLink: (
        <LinkText
          key="integrationConnectionScreenSignUpPrivacyLink"
          target="_blank"
          onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
          isInline={true}
          color="gray"
          to={paths.legal.privacy}
        >
          {t(
            'Link.PrivacyPolicy',
            'Privacy Policy',
            'Privacy Policy link text'
          )}
        </LinkText>
      ),
    }
  );

  const onPasswordResetSubmit = ({ email }: { email: string }) => {
    return AnchorAPI.requestPasswordReset({ emailAddress: email })
      .then(response => {
        const { status } = response;
        if (status === 400) {
          throw new Error('There was a problem with this email.');
        }

        // assume 200 ok
        setCurrentStatus('requestResetPasswordConfirmation');
      })
      .catch(() => {
        throw new Error(
          `There was a problem requesting a password reset. Please reach out to support for more help at ${paths.getHelpPage(
            'home'
          )}.`
        );
      });
  };

  // TODO https://anchorfm.atlassian.net/browse/CAST-2682: When building the IntegrationRedirectScreen,
  // redirect to that route instead of back to the dashboard
  const redirectToDashboard = () => window.location.replace('/');

  const renderForm = () => {
    switch (currentStatus) {
      case 'login':
        return (
          <LoginContainer
            setIntegrationConnectionScreenStatus={setCurrentStatus}
            showSignUp={false}
          />
        );
      case 'requestResetPassword':
        return (
          <RequestResetPasswordContainer
            onVideoLanderSubmit={onPasswordResetSubmit}
            setIntegrationConnectionScreenStatus={setCurrentStatus}
          />
        );
      case 'requestResetPasswordConfirmation':
        return (
          <RequestResetPasswordConfirmationContainer
            setIntegrationConnectionScreenStatus={setCurrentStatus}
          />
        );
      case 'create':
        return (
          <CreateAccountContainer
            enableAutoFocus={false}
            isIntegrationReferral={true}
            isV3Captcha={false}
            submitCallback={() => redirectToDashboard()}
          />
        );
      default:
        return <PageLoadingIndicator color={PODCAST_COLORS.WHITE.color} />;
    }
  };

  return (
    <LoginSignupContainer ref={reference}>
      <AnchorLogoWithWordmark
        ariaLabel={t(
          'svgs.AnchorLogoWithWordmark.BySpotify',
          'by Spotify',
          'Anchor logo with "by Spotify" added after it'
        )}
        includeSpotify={true}
        fillColor={COLOR_BLACK}
      />
      <LoginSignupHeading>
        {t(
          'IntegrationConnectionScreen.LoginSignUp.Heading',
          'Spotify’s free podcasting tool',
          'The heading when a user hits the integration connection screen'
        )}
      </LoginSignupHeading>
      <LoginSignupSubheading>{subheading}</LoginSignupSubheading>
      {renderForm()}
      <LoginSignUpOptionsContainer>
        <LoginSignUpOption>{redirectOption}</LoginSignUpOption>
        <LoginSignUpOption>{importOption}</LoginSignUpOption>
      </LoginSignUpOptionsContainer>
      {isSignup && <LoginSignUpTerms>{signupTerms}</LoginSignUpTerms>}
    </LoginSignupContainer>
  );
}
